import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import { format } from "date-fns";
import { CHANNEL_NAMES } from "../../components/notificationCenter/channelText";
import { MoneyKpis } from "../../components/superadmin/NotificationPanel";
import { PlatformChip } from "../../components/superadmin/SubscriptionChips";
import type { PlatformKey } from "../../services/superadminService";
import {
  KpiCard,
  KpiGrid,
  SA,
  SaCard,
  SaPageHeader,
  StatusPill,
  TONES,
  Tone,
  saTableBodySx,
  saTableHeadSx,
  saToggleSx,
} from "../../components/superadmin/saUi";
import {
  CENTER_CHANNELS,
  CenterChannel,
  ChannelRequest,
  NotificationUsage,
  formatMoney,
  getAllNotificationUsage,
  getNotificationChannelRequests,
  usageRange,
} from "../../services/notificationCenterService";

const CHANNEL_LOOK: Record<CenterChannel, { icon: React.ReactNode; tone: Tone }> = {
  PUSH: { icon: <NotificationsActiveOutlinedIcon />, tone: "violet" },
  EMAIL: { icon: <MailOutlineIcon />, tone: "blue" },
  SMS: { icon: <SmsOutlinedIcon />, tone: "amber" },
  WHATSAPP: { icon: <WhatsAppIcon />, tone: "green" },
};

const Count: React.FC<{ sent: number; failed: number }> = ({ sent, failed }) =>
  sent || failed ? (
    <>
      {sent.toLocaleString()}
      {failed > 0 && (
        <Typography component="span" sx={{ ml: 0.75, fontSize: "0.76rem", color: TONES.red.fg }}>
          {failed} failed
        </Typography>
      )}
    </>
  ) : (
    <Typography component="span" sx={{ color: SA.textSubtle }}>
      —
    </Typography>
  );

/**
 * Messages sent by every organization, per channel, what they were charged and
 * what they cost us, and the organizations waiting for SMS or WhatsApp.
 */
const Messages: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<"this" | "last">("this");
  const [usage, setUsage] = useState<NotificationUsage | null>(null);
  const [requests, setRequests] = useState<ChannelRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getAllNotificationUsage(usageRange(period)).catch(() => null),
      getNotificationChannelRequests().catch(() => []),
    ])
      .then(([u, r]) => {
        setUsage(u);
        setRequests(r);
      })
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(load, [load]);

  const rows = useMemo(() => {
    const list = usage?.byOrganization ?? [];
    const total = (row: (typeof list)[number]) =>
      CENTER_CHANNELS.reduce((sum, c) => sum + row.channels[c].sent + row.channels[c].failed, 0);
    // What gets billed first.
    return [...list].sort((a, b) => b.amount - a.amount || total(b) - total(a));
  }, [usage]);

  const unavailable = !loading && (!usage || !usage.available);

  return (
    <Box>
      <SaPageHeader
        title="Messages"
        subtitle="What each organization sent by channel, what it was charged and what it cost us."
        icon={<ForumOutlinedIcon />}
        actions={
          <>
            <Button
              startIcon={<SellOutlinedIcon />}
              onClick={() => navigate("/superadmin/pricing")}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
            >
              Prices
            </Button>
            <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)} sx={saToggleSx}>
              <ToggleButton value="this">This month</ToggleButton>
              <ToggleButton value="last">Last month</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <IconButton onClick={load} aria-label="Refresh messages">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {requests.length > 0 && (
          <SaCard
            title="Waiting for you to turn on a channel"
            subtitle="Open the organization, then its Messages section, to turn it on."
            icon={<PendingActionsOutlinedIcon />}
            tone="amber"
            flush
          >
            {requests.map((r, index) => (
              <Box
                key={`${r.organizationId}-${r.channel}`}
                component="button"
                type="button"
                onClick={() => navigate(`/superadmin/organizations/${r.organizationId}?tab=messages`)}
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  width: "100%",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: { xs: 2, sm: 2.5 },
                  py: 1.25,
                  borderTop: index ? `1px solid ${SA.divider}` : 0,
                  "&:hover": { bgcolor: SA.surfaceMuted },
                  "&:focus-visible": { outline: `2px solid ${SA.accent}`, outlineOffset: -2 },
                }}
              >
                <Typography sx={{ fontWeight: 600, color: SA.text, flex: 1, minWidth: 0 }}>{r.organizationName}</Typography>
                <StatusPill tone={CHANNEL_LOOK[r.channel].tone}>{CHANNEL_NAMES[r.channel]}</StatusPill>
                <Typography sx={{ fontSize: "0.82rem", color: SA.textMuted, whiteSpace: "nowrap" }}>
                  asked {format(new Date(r.requestedAt), "d MMM yyyy")}
                </Typography>
              </Box>
            ))}
          </SaCard>
        )}

        {unavailable ? (
          <SaCard>
            <Typography sx={{ color: SA.textMuted }}>
              Counts are not available right now: the notification service could not be reached.
            </Typography>
          </SaCard>
        ) : (
          <>
            <KpiGrid min={180}>
              {CENTER_CHANNELS.map((channel) => {
                const counts = usage?.totals?.[channel];
                const paid = channel === "SMS" || channel === "WHATSAPP";
                return (
                  <KpiCard
                    key={channel}
                    label={CHANNEL_NAMES[channel]}
                    icon={CHANNEL_LOOK[channel].icon}
                    tone={CHANNEL_LOOK[channel].tone}
                    value={loading || !counts ? "…" : counts.sent.toLocaleString()}
                    captionTone={counts?.failed ? "red" : undefined}
                    caption={
                      loading || !counts
                        ? " "
                        : [
                            counts.failed ? `${counts.failed.toLocaleString()} failed` : "none failed",
                            paid ? `${formatMoney(counts.amount, usage?.currency)} charged` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")
                    }
                  />
                );
              })}
            </KpiGrid>
            {usage && !loading && <MoneyKpis usage={usage} />}

            <SaCard
              title="By organization"
              subtitle="Most charged first. Click a row to open the organization."
              icon={<ApartmentOutlinedIcon />}
              tone="slate"
              flush
            >
              {loading ? (
                <Box sx={{ p: 2.5 }}>
                  <Skeleton height={28} />
                  <Skeleton height={28} />
                  <Skeleton height={28} />
                </Box>
              ) : rows.length === 0 ? (
                <Typography sx={{ p: 2.5, color: SA.textMuted }}>No messages in this period.</Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 860 }}>
                    <TableHead sx={saTableHeadSx}>
                      <TableRow>
                        <TableCell>Organization</TableCell>
                        {CENTER_CHANNELS.map((c) => (
                          <TableCell key={c} align="right">
                            {CHANNEL_NAMES[c]}
                          </TableCell>
                        ))}
                        <TableCell align="right">Charged</TableCell>
                        <TableCell align="right">Our cost</TableCell>
                        <TableCell align="right">Margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={saTableBodySx}>
                      {rows.map((row) => {
                        const margin = row.amount - (row.cost ?? 0);
                        return (
                          <TableRow
                            key={row.organizationId}
                            hover
                            onClick={() => navigate(`/superadmin/organizations/${row.organizationId}?tab=messages`)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: SA.text }}>
                                  {row.organizationName ?? row.organizationId}
                                </Typography>
                                {row.platform && <PlatformChip platform={row.platform as PlatformKey} />}
                              </Box>
                            </TableCell>
                            {CENTER_CHANNELS.map((c) => (
                              <TableCell key={c} align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                <Count sent={row.channels[c].sent} failed={row.channels[c].failed} />
                              </TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                              {formatMoney(row.amount, usage?.currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: `${SA.textMuted} !important` }}>
                              {formatMoney(row.cost, usage?.currency)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontVariantNumeric: "tabular-nums",
                                fontWeight: 600,
                                color: `${margin < 0 ? TONES.red.fg : TONES.green.fg} !important`,
                              }}
                            >
                              {formatMoney(margin, usage?.currency)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </SaCard>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Messages;
